"use client"

import { useState, useEffect, useRef } from "react"
import {
  Activity,
  BarChart3,
  Shield,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  ArrowDown,
  Menu,
  X,
  Play,
  Users,
  Target,
  Mail,
  Phone,
  ChevronDown,
  Calendar,
  Send,
  Eye,
  Zap,
  Brain,
  FileCheck,
  GitBranch,
  AlertTriangle,
  Clock,
  DollarSign,
  Factory,
  Truck,
  Briefcase,
  HeartPulse,
} from "lucide-react"
import "./App.css"

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeFeature, setActiveFeature] = useState(0)
  const [activeFAQ, setActiveFAQ] = useState(-1)
  const countersAnimated = useRef(false)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    jobTitle: "",
    industry: "",
    location: "",
    phone: "",
    message: "",
  })
  const [formSubmitted, setFormSubmitted] = useState(false)

  // Auto-cycle through features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 5)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in")

            if (entry.target.classList.contains("stats-container") && !countersAnimated.current) {
              animateCounters()
            }
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = document.querySelectorAll(".observe-me")
    elements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  const animateCounters = () => {
    if (countersAnimated.current) return
    countersAnimated.current = true

    const counters = document.querySelectorAll(".counter")
    counters.forEach((counter) => {
      const target = Number.parseInt(counter.getAttribute("data-target"))
      const increment = target / 60
      let current = 0

      const updateCounter = () => {
        if (current < target) {
          current += increment
          counter.textContent = Math.ceil(current)
          setTimeout(updateCounter, 30)
        } else {
          counter.textContent = target
        }
      }
      updateCounter()
    })
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
    setMobileMenuOpen(false)
  }

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? -1 : index)
  }

  const features = [
    {
      icon: <Eye size={32} />,
      title: "End-to-End Visibility",
      description: "Full visualization of real operational flows with automatic identification of hidden bottlenecks",
    },
    {
      icon: <Zap size={32} />,
      title: "Real-Time Control",
      description: "Intelligent alerts and continuous monitoring to fix deviations before they impact results",
    },
    {
      icon: <Brain size={32} />,
      title: "Predictive Simulation",
      description: "Test scenarios without risk and assess impact on costs, cycle time, and CO₂ footprint",
    },
    {
      icon: <Target size={32} />,
      title: "AI-Powered Actions",
      description: "Automatic detection of inefficiencies with proactive improvement recommendations",
    },
    {
      icon: <FileCheck size={32} />,
      title: "Verifiable Traceability",
      description: "Immutable records and full lifecycle traceability for compliance and audits",
    },
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleFormSubmit = (e) => {
    e.preventDefault()
    console.log("Demo form submitted:", formData)
    setFormSubmitted(true)

    setTimeout(() => {
      setFormSubmitted(false)
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        jobTitle: "",
        industry: "",
        location: "",
        phone: "",
        message: "",
      })
    }, 3000)
  }

  return (
    <div className="energy-ai-app">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <nav className="nav-landing">
            <div className="nav-brand">
              <div className="nav-logo-icon">
                <Activity size={32} />
              </div>
              <span className="nav-brand-text">Lumenn</span>
            </div>

            {/* Desktop Navigation */}
            <div className="nav-menu desktop-nav">
              <button onClick={() => scrollToSection("inicio")} className="nav-link">
                Home
              </button>
              <button onClick={() => scrollToSection("problem")} className="nav-link">
                The Problem
              </button>
              <button onClick={() => scrollToSection("solution")} className="nav-link">
                Solution
              </button>
              <button onClick={() => scrollToSection("benefits")} className="nav-link">
                Benefits
              </button>
              <button onClick={() => scrollToSection("results")} className="nav-link">
                Results
              </button>
              <button onClick={() => scrollToSection("faq")} className="nav-link">
                FAQ
              </button>
              <button onClick={() => scrollToSection("demo")} className="nav-link">
                Request Demo
              </button>
            </div>

            {/* Desktop Actions */}
            <div className="nav-actions desktop-nav">
              <button className="btn-nav-secondary">Log In</button>
            </div>

            {/* Mobile Menu Button */}
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="mobile-nav">
              <div className="mobile-nav-links">
                <button onClick={() => scrollToSection("inicio")} className="mobile-nav-link">
                  Home
                </button>
                <button onClick={() => scrollToSection("problem")} className="mobile-nav-link">
                  The Problem
                </button>
                <button onClick={() => scrollToSection("solution")} className="mobile-nav-link">
                  Solution
                </button>
                <button onClick={() => scrollToSection("benefits")} className="mobile-nav-link">
                  Benefits
                </button>
                <button onClick={() => scrollToSection("results")} className="mobile-nav-link">
                  Results
                </button>
                <button onClick={() => scrollToSection("faq")} className="mobile-nav-link">
                  FAQ
                </button>
                <button onClick={() => scrollToSection("demo")} className="mobile-nav-link">
                  Request Demo
                </button>
              </div>
              <div className="mobile-nav-actions">
                <button className="btn-mobile-secondary">Log In</button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-energy" id="inicio">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-main">
            <h1 className="hero-title observe-me">
              Process Intelligence Platform for
              <span className="gradient-text"> Real-Time Operational Control</span>
            </h1>

            <p className="hero-subtitle observe-me">
              Full visibility into your operations. Total control. Decisions without uncertainty. Transform real
              operational data into a single source of truth.
            </p>

            <div className="hero-actions observe-me">
              <button onClick={() => scrollToSection("demo")} className="btn-primary-hero">
                <Play size={20} />
                Request a Demo
              </button>
              <button onClick={() => scrollToSection("solution")} className="btn-secondary-hero">
                <ArrowRight size={20} />
                See How It Works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="platform-section" id="problem">
        <div className="container">
          <div className="section-header observe-me">
            <h2>Hidden Inefficiencies, Real Costs</h2>
            <p>
              Every year, companies lose millions by operating blindly: fragmented data, processes that don't run as
              designed, and decisions based on assumptions instead of facts.
            </p>
          </div>

          <div className="platform-content observe-me">
            <div className="platform-text">
              <h3>The Invisible Gap Killing Performance</h3>
              <p>
                Only 20% of operational inefficiencies are visible with traditional tools. Enterprises run on 40+
                disconnected systems, creating massive data silos.
              </p>

              <div className="benefits-list">
                <div className="benefit-item">
                  <AlertTriangle size={20} />
                  <span>Only 20% of operational inefficiencies are visible with traditional tools</span>
                </div>
                <div className="benefit-item">
                  <AlertTriangle size={20} />
                  <span>Enterprises run on 40+ disconnected systems, creating massive data silos</span>
                </div>
                <div className="benefit-item">
                  <AlertTriangle size={20} />
                  <span>30–50% of process time is wasted on manual tasks that could be automated</span>
                </div>
                <div className="benefit-item">
                  <AlertTriangle size={20} />
                  <span>Higher costs, delayed decisions, compliance risks, and missed improvement opportunities</span>
                </div>
              </div>

              <div className="problem-highlight">
                <p>Process design on paper ≠ real execution in operations</p>
              </div>
            </div>

            <div className="features-grid">
              <div className="problem-card">
                <div className="problem-icon">
                  <GitBranch size={40} />
                </div>
                <h4>Fragmented Data</h4>
                <p>Systems that don't talk to each other create blind spots</p>
              </div>

              <div className="problem-card">
                <div className="problem-icon">
                  <Clock size={40} />
                </div>
                <h4>Hidden Inefficiencies</h4>
                <p>Bottlenecks and waste remain invisible until it's too late</p>
              </div>

              <div className="problem-card">
                <div className="problem-icon">
                  <DollarSign size={40} />
                </div>
                <h4>Rising Costs</h4>
                <p>Operating without clarity drives up operational expenses</p>
              </div>

              <div className="problem-card">
                <div className="problem-icon">
                  <Shield size={40} />
                </div>
                <h4>Compliance Risks</h4>
                <p>Lack of traceability creates regulatory vulnerabilities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solutions-section" id="solution">
        <div className="container">
          <div className="section-header observe-me">
            <h2>The Solution: A Live, End-to-End View of Your Operations</h2>
            <p>
              Our platform creates a living digital twin of your operations, connecting systems, processes, and
              stakeholders into one unified, real-time view.
            </p>
          </div>

          <div className="solutions-grid observe-me">
            <div className="solution-card">
              <div className="solution-icon">
                <Eye size={48} />
              </div>
              <h3>See How Processes Actually Run</h3>
              <p>Not how they were planned — understand the reality of your operations in real-time</p>
            </div>

            <div className="solution-card">
              <div className="solution-icon">
                <Target size={48} />
              </div>
              <h3>Automatic Bottleneck Detection</h3>
              <p>Identify inefficiencies instantly without manual analysis or guesswork</p>
            </div>

            <div className="solution-card">
              <div className="solution-icon">
                <BarChart3 size={48} />
              </div>
              <h3>Understand Real Impact</h3>
              <p>Know the financial, operational, and ESG impact of every decision you make</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section" id="benefits">
        <div className="container">
          <div className="section-header observe-me">
            <h2>Key Benefits: What You Gain, Not How It Works</h2>
            <p>Transform your operations with comprehensive visibility and intelligent automation</p>
          </div>

          <div className="benefits-main observe-me">
            <div className="benefits-sidebar">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`benefit-tab ${activeFeature === index ? "active" : ""}`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="benefit-tab-icon">{feature.icon}</div>
                  <div className="benefit-tab-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="benefits-display">
              <div className="benefit-detail">
                <div className="benefit-detail-icon">{features[activeFeature].icon}</div>
                <h3>{features[activeFeature].title}</h3>
                <p className="benefit-detail-description">{features[activeFeature].description}</p>

                <div className="benefit-features-list">
                  {activeFeature === 0 && (
                    <>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Full visualization of real operational flows</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Automatic identification of hidden bottlenecks</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Granular metrics for time, cost, and critical events</span>
                      </div>
                    </>
                  )}

                  {activeFeature === 1 && (
                    <>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Intelligent alerts when thresholds are exceeded</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Continuous comparison of real execution vs. targets</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Immediate corrective action instead of reactive firefighting</span>
                      </div>
                    </>
                  )}

                  {activeFeature === 2 && (
                    <>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Simulate changes in suppliers, materials, or logistics routes</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Instantly assess impact on costs, cycle time, and CO₂ footprint</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Compare scenarios side by side and choose the optimal strategy</span>
                      </div>
                    </>
                  )}

                  {activeFeature === 3 && (
                    <>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Automatic detection of inefficiencies and anomalies</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Proactive improvement recommendations</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Smart prioritization based on financial and operational impact</span>
                      </div>
                    </>
                  )}

                  {activeFeature === 4 && (
                    <>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Immutable event records across production and logistics</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Full lifecycle traceability from raw material to final delivery</span>
                      </div>
                      <div className="benefit-feature">
                        <CheckCircle size={20} />
                        <span>Auditable, transparent data for regulators, customers, and partners</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="results-section" id="results">
        <div className="container">
          <div className="section-header observe-me">
            <h2>Proven Business Impact</h2>
            <p>
              Measurable results, not promises. Organizations using process intelligence achieve transformative
              outcomes.
            </p>
          </div>

          <div className="stats-grid observe-me stats-container">
            <div className="stat-card primary">
              <div className="stat-icon">
                <TrendingUp size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  +<span className="counter" data-target="30">0</span>%
                </div>
                <div className="stat-label">Operational efficiency</div>
                <div className="stat-description">Streamlined processes</div>
              </div>
            </div>

            <div className="stat-card secondary">
              <div className="stat-icon">
                <DollarSign size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  −<span className="counter" data-target="20">0</span>%
                </div>
                <div className="stat-label">Operational costs</div>
                <div className="stat-description">Direct cost savings</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">
                <Target size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <span className="counter" data-target="43">0</span>%
                </div>
                <div className="stat-label">Bottleneck reduction</div>
                <div className="stat-description">Eliminated inefficiencies</div>
              </div>
            </div>

            <div className="stat-card accent">
              <div className="stat-icon">
                <Clock size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  −<span className="counter" data-target="28">0</span>%
                </div>
                <div className="stat-label">Cycle time reduction</div>
                <div className="stat-description">Faster execution</div>
              </div>
            </div>
          </div>

          <div className="enterprise-badge observe-me">
            <p>
              Over <strong>60% of Fortune 500 companies</strong> already rely on process intelligence to drive
              measurable business outcomes
            </p>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="integration-section">
        <div className="container">
          <div className="section-header observe-me">
            <h2>Built to Fit Your Ecosystem, Not Replace It</h2>
            <p>Seamless integration with your existing systems. Fast time to value without disruption.</p>
          </div>

          <div className="integration-content observe-me">
            <div className="integration-text">
              <h3>Connect Everything, Disrupt Nothing</h3>
              <p>
                Our platform integrates seamlessly with ERP, CRM, MES, WMS, IoT, SCADA, and spreadsheets through APIs
                and native connectors. Scale effortlessly as you add processes, plants, or partners.
              </p>

              <div className="timeline-box">
                <div className="timeline-item">
                  <div className="timeline-marker">4</div>
                  <div className="timeline-content">
                    <h4>Weeks</h4>
                    <p>First insights delivered</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-marker">60</div>
                  <div className="timeline-content">
                    <h4>Days</h4>
                    <p>Tangible improvements achieved</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="integration-visual">
              <div className="integration-systems">
                <div className="system-badge">ERP</div>
                <div className="system-badge">CRM</div>
                <div className="system-badge">MES</div>
                <div className="system-badge">WMS</div>
                <div className="system-badge">IoT</div>
                <div className="system-badge">SCADA</div>
                <div className="system-badge">Excel</div>
                <div className="system-badge">Databases</div>
              </div>
              <div className="integration-arrow">
                <ArrowDown size={40} />
              </div>
              <div className="integration-platform">
                <Activity size={48} />
                <p>Lumenn Platform</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="usecases-section">
        <div className="container">
          <div className="section-header observe-me">
            <h2>One Platform, Unlimited Use Cases</h2>
            <p>Proven across industries to deliver operational excellence</p>
          </div>

          <div className="usecases-grid observe-me">
            <div className="usecase-card">
              <div className="usecase-icon">
                <Factory size={40} />
              </div>
              <h3>Manufacturing</h3>
              <p>Reduced waste and higher throughput with real-time production visibility</p>
            </div>

            <div className="usecase-card">
              <div className="usecase-icon">
                <Truck size={40} />
              </div>
              <h3>Supply Chain & Logistics</h3>
              <p>Full visibility and on-time delivery across the entire value chain</p>
            </div>

            <div className="usecase-card">
              <div className="usecase-icon">
                <Briefcase size={40} />
              </div>
              <h3>Finance & Procurement</h3>
              <p>Elimination of rework and duplicate payments through process automation</p>
            </div>

            <div className="usecase-card">
              <div className="usecase-icon">
                <HeartPulse size={40} />
              </div>
              <h3>Healthcare & Life Sciences</h3>
              <p>Optimized administrative and clinical workflows for better patient outcomes</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <div className="faq-header observe-me">
            <h2>Frequently Asked Questions</h2>
            <p>Learn more about our Process Intelligence Platform</p>
          </div>

          <div className="faq-content observe-me">
            <div className="faq-visual">
              <div className="faq-image-container">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="Process intelligence dashboard and analytics"
                />
              </div>
            </div>

            <div className="faq-list">
              {[
                {
                  question: "How does Lumenn differ from traditional business intelligence tools?",
                  answer:
                    "Unlike traditional BI tools that only show historical data, Lumenn creates a living digital twin of your operations with real-time process mining and predictive capabilities. We don't just tell you what happened—we show you what's happening now and help you prevent future issues.",
                },
                {
                  question: "How quickly can we see results?",
                  answer:
                    "Most organizations see their first actionable insights within 4 weeks of implementation. Tangible operational improvements typically occur within 60 days. Our non-intrusive integration means you can start gaining value without disrupting existing workflows.",
                },
                {
                  question: "What systems does Lumenn integrate with?",
                  answer:
                    "Lumenn connects seamlessly with ERP, CRM, MES, WMS, IoT sensors, SCADA systems, databases, and even spreadsheets. Our platform uses APIs and native connectors for non-intrusive integration that scales with your needs.",
                },
                {
                  question: "Is our data secure and compliant?",
                  answer:
                    "Absolutely. Lumenn provides immutable event records with full lifecycle traceability, making compliance, audits, and ESG reporting simple and defensible. All data is encrypted and stored with enterprise-grade security standards.",
                },
              ].map((faq, index) => (
                <div key={index} className="faq-item">
                  <button
                    className={`faq-question ${activeFAQ === index ? "active" : ""}`}
                    onClick={() => toggleFAQ(index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={20} className={`faq-icon ${activeFAQ === index ? "rotated" : ""}`} />
                  </button>
                  <div className={`faq-answer ${activeFAQ === index ? "active" : ""}`}>
                    <div className="faq-answer-content">{faq.answer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="demo-section" id="demo">
        <div className="container">
          <div className="demo-content observe-me">
            <div className="demo-header">
              <h2>Stop Operating with Uncertainty</h2>
              <p>
                Schedule a personalized demo and unlock the hidden potential of your processes. See how Lumenn
                transforms operational data into actionable intelligence.
              </p>
            </div>

            <div className="demo-form-container">
              {!formSubmitted ? (
                <form className="demo-form" onSubmit={handleFormSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Business Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@company.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company">Company *</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        placeholder="Your company name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="jobTitle">Job Title *</label>
                      <input
                        type="text"
                        id="jobTitle"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Operations Director"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="industry">Industry *</label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select your industry</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="supply-chain">Supply Chain & Logistics</option>
                        <option value="finance">Finance & Procurement</option>
                        <option value="healthcare">Healthcare & Life Sciences</option>
                        <option value="energy">Energy & Utilities</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="location">Primary Location *</label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        placeholder="City, State/Country"
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="message">Tell us about your operational challenges</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Describe your current process visibility challenges, operational inefficiencies, or specific use cases you'd like to explore..."
                    ></textarea>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-form-primary">
                      <Send size={20} />
                      Request Demo
                    </button>
                    <p className="form-note">
                      * Required fields. We'll contact you within 24 hours to schedule your personalized demo.
                    </p>
                  </div>
                </form>
              ) : (
                <div className="form-success">
                  <div className="success-icon">
                    <CheckCircle size={64} />
                  </div>
                  <h3>Demo Request Submitted!</h3>
                  <p>
                    Thank you for your interest in Lumenn. Our team will contact you within 24 hours to schedule
                    your personalized demo and show you how we can transform your operations.
                  </p>
                  <div className="success-details">
                    <div className="detail-item">
                      <Calendar size={20} />
                      <span>Demo scheduled within 24 hours</span>
                    </div>
                    <div className="detail-item">
                      <Users size={20} />
                      <span>Dedicated process intelligence expert</span>
                    </div>
                    <div className="detail-item">
                      <BarChart3 size={20} />
                      <span>Customized insights for your industry</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="demo-benefits">
              <h3>What to expect in your demo:</h3>
              <div className="benefits-grid">
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <Eye size={24} />
                  </div>
                  <h4>Live Process Visualization</h4>
                  <p>See how your actual operations flow in real-time</p>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <Target size={24} />
                  </div>
                  <h4>Bottleneck Analysis</h4>
                  <p>Identify hidden inefficiencies specific to your processes</p>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <DollarSign size={24} />
                  </div>
                  <h4>ROI Calculation</h4>
                  <p>Customized business case with expected savings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-energy">
        <div className="container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <Activity size={32} />
                <span>Lumenn</span>
              </div>
              <p>A new way to understand and manage operations with full visibility, better decisions, and demonstrable trust.</p>
              <div className="footer-contact">
                <div className="contact-method">
                  <Mail size={16} />
                  <span>contact@Lumenn.com</span>
                </div>
                <div className="contact-method">
                  <Phone size={16} />
                  <span>+1 (555) 123-4567</span>
                </div>
              </div>
            </div>

            <div className="footer-links">
              <div className="link-group">
                <h4>Platform</h4>
                <ul>
                  <li>
                    <button onClick={() => scrollToSection("solution")}>Features</button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection("benefits")}>Benefits</button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection("demo")}>Request Demo</button>
                  </li>
                  <li>
                    <button>API Documentation</button>
                  </li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Industries</h4>
                <ul>
                  <li>
                    <button>Manufacturing</button>
                  </li>
                  <li>
                    <button>Supply Chain</button>
                  </li>
                  <li>
                    <button>Finance</button>
                  </li>
                  <li>
                    <button>Healthcare</button>
                  </li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Company</h4>
                <ul>
                  <li>
                    <button>About Us</button>
                  </li>
                  <li>
                    <button>Case Studies</button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection("demo")}>Contact</button>
                  </li>
                  <li>
                    <button>Support</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Lumenn. Transforming operations through intelligent process visibility.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage